import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, ShoppingCart, Package, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: product.main_category || 'general'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

// Normalize text for search (remove accents, lowercase)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .trim();
};

// Search scoring function - prioritizes parts over spas
const getSearchScore = (item, searchTerms, isProduct = true) => {
  let score = 0;
  const name = normalizeText(item.name);
  const brand = normalizeText(item.brand);
  const sku = normalizeText(item.sku);
  const model = normalizeText(item.model);
  const subCategory = normalizeText(item.sub_category);
  const shortDesc = normalizeText(item.short_description);
  
  for (const term of searchTerms) {
    // Exact matches in SKU/model get highest score
    if (sku === term || model === term) {
      score += 100;
    } else if (sku?.includes(term) || model?.includes(term)) {
      score += 50;
    }
    
    // Name matches
    if (name?.includes(term)) {
      score += 30;
      if (name.startsWith(term) || name.includes(` ${term}`)) {
        score += 20;
      }
    }
    
    // Brand matches
    if (brand?.includes(term)) {
      score += 25;
    }
    
    // Category matches
    if (subCategory?.includes(term)) {
      score += 15;
    }
    
    // Description matches
    if (shortDesc?.includes(term)) {
      score += 5;
    }
  }
  
  // Prioritize parts/accessories over spas
  if (isProduct) {
    score += 10; // Boost for parts
  }
  
  return score;
};

/**
 * Global Header Search Component
 * Searches across parts, accessories, filters, and spas
 */
const HeaderSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ parts: [], spas: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [allParts, setAllParts] = useState([]);
  const [allSpas, setAllSpas] = useState([]);
  
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partsRes, spasRes] = await Promise.all([
          axios.get(`${API}/parts`),
          axios.get(`${API}/spas`)
        ]);
        setAllParts(partsRes.data || []);
        setAllSpas(spasRes.data || []);
      } catch (error) {
        console.error('Error fetching search data:', error);
      }
    };
    fetchData();
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Perform search
  const performSearch = useCallback((query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults({ parts: [], spas: [] });
      return;
    }

    setIsSearching(true);
    
    const searchTerms = normalizeText(query).split(/\s+/).filter(t => t.length >= 2);
    
    // Search parts (prioritized)
    const partsResults = allParts
      .map(item => ({
        item,
        score: getSearchScore(item, searchTerms, true)
      }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(r => r.item);
    
    // Search spas (secondary)
    const spasResults = allSpas
      .map(item => ({
        item,
        score: getSearchScore(item, searchTerms, false)
      }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(r => r.item);
    
    setSearchResults({ parts: partsResults, spas: spasResults });
    setIsSearching(false);
  }, [allParts, allSpas]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults({ parts: [], spas: [] });
  };

  const closeAndNavigate = (path) => {
    setIsOpen(false);
    setSearchQuery('');
    navigate(path);
  };

  const totalResults = searchResults.parts.length + searchResults.spas.length;

  return (
    <div className="relative" ref={searchRef} data-testid="header-search">
      {/* Search Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-[#475569] hover:text-[#1E3A5F] hover:bg-[#F8FAFC] rounded-lg transition-colors"
        aria-label="Rechercher"
        data-testid="header-search-toggle"
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Search Overlay */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[90vw] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50">
          {/* Search Input */}
          <div className="p-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Search className={`w-5 h-5 flex-shrink-0 ${isSearching ? 'text-[#F97316] animate-pulse' : 'text-slate-400'}`} />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher une pièce, une référence, un accessoire…"
                className="flex-1 text-base text-slate-900 placeholder-slate-400 focus:outline-none"
                data-testid="header-search-input"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="max-h-[60vh] overflow-y-auto">
            {searchQuery.length >= 2 ? (
              totalResults > 0 ? (
                <>
                  {/* Parts Results (Prioritized) */}
                  {searchResults.parts.length > 0 && (
                    <div>
                      <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Pièces & Accessoires ({searchResults.parts.length})
                      </div>
                      {searchResults.parts.map((item) => (
                        <div 
                          key={item.slug}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                          onClick={() => closeAndNavigate(`/pieces-accessoires/${item.slug}`)}
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {item.images?.[0] ? (
                              <img 
                                src={item.images[0]} 
                                alt={item.name}
                                className="w-full h-full object-contain p-1"
                              />
                            ) : (
                              <Package className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-500 uppercase">{item.brand}</p>
                            <h4 className="text-sm font-medium text-slate-900 truncate">{item.name}</h4>
                            {item.sku && <p className="text-xs text-slate-400">Réf: {item.sku}</p>}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            {item.price_ttc ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-900">{formatPrice(item.price_ttc)}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(item);
                                  }}
                                  className="p-1.5 bg-[#1E3A5F] hover:bg-[#152C4A] text-white rounded-lg transition-colors"
                                >
                                  <ShoppingCart className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">Sur devis</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Spas Results (Secondary) */}
                  {searchResults.spas.length > 0 && (
                    <div>
                      <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        Spas ({searchResults.spas.length})
                      </div>
                      {searchResults.spas.map((spa) => (
                        <div 
                          key={spa.slug}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                          onClick={() => closeAndNavigate(`/spas/${spa.slug}`)}
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {spa.images?.[0] ? (
                              <img 
                                src={spa.images[0]} 
                                alt={spa.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Package className="w-5 h-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-slate-500 uppercase">{spa.brand}</p>
                            <h4 className="text-sm font-medium text-slate-900 truncate">{spa.name}</h4>
                            <p className="text-xs text-slate-400">{spa.places} places</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* See all results link */}
                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                    <Link 
                      to={`/pieces-detachees?q=${encodeURIComponent(searchQuery)}`}
                      onClick={() => setIsOpen(false)}
                      className="text-sm text-[#1E3A5F] font-medium hover:underline flex items-center gap-1"
                    >
                      Voir tous les résultats
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </>
              ) : (
                <div className="p-6 text-center">
                  <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium mb-1">Aucun résultat</p>
                  <p className="text-sm text-slate-500">
                    Essayez avec une autre référence ou mot-clé
                  </p>
                </div>
              )
            ) : (
              <div className="p-6 text-center">
                <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">Recherche rapide</p>
                <p className="text-sm text-slate-500">
                  Tapez au moins 2 caractères pour rechercher
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
